/**
 * Shared SLA formatting utilities.
 * UI mode: null/undefined → em-dash (U+2014). 0 is NOT empty.
 * CSV mode: null/undefined → ASCII hyphen (-).
 */

export type FormatMode = 'ui' | 'csv'

const EM_DASH = '—'
const CSV_EMPTY = '-'

function emptyMark(mode: FormatMode): string {
    return mode === 'ui' ? EM_DASH : CSV_EMPTY
}

/**
 * Format a percentage number.
 * Primary KPI display: .toFixed(2). Badge density: .toFixed(1).
 * Never uses Math.round — raw float for threshold comparisons.
 */
export function formatPercent(
    value: number | null | undefined,
    mode: FormatMode = 'ui',
    precision: 1 | 2 = 2
): string {
    if (value === null || value === undefined || isNaN(value as number)) {
        return emptyMark(mode)
    }
    return `${(value as number).toFixed(precision)}%`
}

/**
 * Format minutes to human-readable duration.
 * null/undefined → em-dash (UI) or '-' (CSV).
 * 0 → '0m' (not empty).
 */
export function formatMinutes(
    minutes: number | null | undefined,
    mode: FormatMode = 'ui'
): string {
    if (minutes === null || minutes === undefined || isNaN(minutes as number)) {
        return emptyMark(mode)
    }
    const m = minutes as number
    if (m === 0) return '0m'
    if (m < 60) return `${m}m`
    const hours = Math.floor(m / 60)
    const mins = m % 60
    if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

/**
 * Generic value formatter. Falls back to string coercion.
 */
export function formatValue(
    value: number | string | null | undefined,
    mode: FormatMode = 'ui'
): string {
    if (value === null || value === undefined || value === '') {
        return emptyMark(mode)
    }
    return String(value)
}

/**
 * Compliance threshold check — uses raw float, never rounded.
 * Returns true when value >= threshold (e.g. 95.0).
 */
export function meetsThreshold(
    value: number | null | undefined,
    threshold: number
): boolean {
    if (value === null || value === undefined || isNaN(value as number)) {
        return false
    }
    return (value as number) >= threshold
}
